import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import {
  PageHeader,
  RiskBadge,
  StageBadge,
  StatusBadge,
  PortfolioPriorityBadge,
  EmptyState,
} from '@/components/common/Bits';
import { exportCSV, fmtDate } from '@/lib/constants';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Search01Icon,
  Add01Icon,
  Download04Icon,
  GridViewIcon,
  Menu01Icon,
  Database01Icon,
  TestTube01Icon,
  ArrowUp01Icon,
  ArrowDown01Icon,
  StethoscopeIcon,
  BalanceScaleIcon,
  MoneyBag02Icon,
  UserMultipleIcon,
  Settings02Icon,
  HospitalIcon,
  ComputerIcon,
  MoreVerticalIcon,
  PauseIcon,
  PlayIcon,
  Delete02Icon,
  Archive02Icon,
  InboxIcon,
} from '@hugeicons/core-free-icons';
import { toast } from 'sonner';

const ALL = '__all__';

const DEPT_ICONS = {
  Medicina: { icon: StethoscopeIcon, color: 'text-cyan-600' },
  Legal: { icon: BalanceScaleIcon, color: 'text-violet-600' },
  Finanzas: { icon: MoneyBag02Icon, color: 'text-emerald-600' },
  'Recursos Humanos': { icon: UserMultipleIcon, color: 'text-pink-600' },
  'Operaciones Clínicas': { icon: HospitalIcon, color: 'text-blue-600' },
  'Gestión Clínica': { icon: Settings02Icon, color: 'text-amber-600' },
  Tecnología: { icon: ComputerIcon, color: 'text-indigo-600' },
};

export default function Registry() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [catalogs, setCatalogs] = useState(null);
  const [view, setView] = useState('table');
  const [density, setDensity] = useState('comfortable');
  const [q, setQ] = useState('');
  // Filtros iniciales: permiten drill-down desde el Panel principal vía query
  // params, p.ej. /registro?stage=Producción o ?department=Finanzas.
  const [filters, setFilters] = useState(() => ({
    department: searchParams.get('department') || ALL,
    stage: searchParams.get('stage') || ALL,
    risk: searchParams.get('risk') || ALL,
    provider: searchParams.get('provider') || ALL,
    data_classification: ALL,
  }));
  const [sort, setSort] = useState('-updated_at');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  // Filtro especial de drill-down desde el Panel: ?evaluacion=abierta muestra
  // solo iniciativas con evaluación en curso o pendiente (coincide con el conteo
  // de "Evaluaciones abiertas" del dashboard). Se aplica en cliente porque el
  // backend filtra evaluation_status por un único valor exacto.
  const evaluacionFilter = searchParams.get('evaluacion');

  const load = async () => {
    const params = { sort };
    if (q) params.q = q;
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== ALL) params[k] = v;
    });
    const { data } = await api.get('/initiatives', { params });
    let result = data;
    if (evaluacionFilter === 'abierta') {
      result = data.filter((r) => r.evaluation_status === 'En curso' || r.evaluation_status === 'Pendiente');
    }
    setRows(result);
  };

  const executeAction = async (id, action) => {
    try {
      await api.post(`/initiatives/${id}/actions`, { action });
      const labels = {
        pause: 'Iniciativa pausada',
        resume: 'Iniciativa retomada',
        archive: 'Iniciativa archivada',
        return_to_demand: 'Devuelta a bandeja de entrada',
      };
      toast.success(labels[action] || 'Acción ejecutada');
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteInitiative = async (id) => {
    try {
      await api.delete(`/initiatives/${id}`);
      toast.success('Iniciativa eliminada');
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    api.get('/catalogs').then(({ data }) => setCatalogs(data));
  }, []);
  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, filters, sort, evaluacionFilter]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [q, filters]);

  const filterDefs = useMemo(
    () =>
      catalogs
        ? [
            { key: 'department', label: 'Departamento', options: catalogs.departments },
            { key: 'stage', label: 'Etapa', options: catalogs.stages },
            { key: 'risk', label: 'Riesgo', options: catalogs.risk_levels },
            { key: 'provider', label: 'Proveedor', options: catalogs.providers },
            { key: 'data_classification', label: 'Clasificación', options: catalogs.data_classifications },
          ]
        : [],
    [catalogs],
  );

  const doExport = () => {
    exportCSV(
      rows.map((r) => ({
        codigo: r.code,
        nombre: r.name,
        departamento: r.department,
        responsable: r.business_owner,
        proveedor: r.provider,
        modelo: r.model,
        etapa: r.stage,
        riesgo_inherente: r.inherent_risk,
        riesgo_residual: r.residual_risk,
        comite: r.committee_status,
        clasificacion: r.data_classification,
        valor_esperado: r.expected_value,
        proxima_revision: r.next_review_date,
      })),
      'ai_registry_ucchristus.csv',
    );
    toast.success('Registro exportado a CSV');
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-6">
      <PageHeader
        title="AI Registry"
        subtitle="Catálogo institucional de iniciativas, herramientas, modelos y agentes de IA."
        testId="registry-header">
        <Button
          variant="outline"
          onClick={doExport}
          disabled={!rows?.length}
          data-testid="export-registry-btn"
          className="border-border">
          <HugeiconsIcon icon={Download04Icon} size={16} className="mr-2" /> Exportar
        </Button>
        {can.edit && (
          <Button
            onClick={() => navigate('/registro/nueva')}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
            data-testid="new-initiative-btn">
            <HugeiconsIcon icon={Add01Icon} size={16} className="mr-2" /> Nueva iniciativa
          </Button>
        )}
      </PageHeader>

      <div className="flex flex-col gap-3 mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-56">
            <HugeiconsIcon
              icon={Search01Icon}
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por iniciativa, responsable, departamento, proveedor o modelo…"
              className="pl-9 bg-card"
              data-testid="registry-search"
            />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-44 bg-card" data-testid="registry-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="-updated_at">Más recientes</SelectItem>
              <SelectItem value="name">Nombre A–Z</SelectItem>
              <SelectItem value="-expected_value_clp">Mayor valor</SelectItem>
              <SelectItem value="department">Departamento</SelectItem>
            </SelectContent>
          </Select>
          <ToggleGroup
            type="single"
            value={view}
            onValueChange={(v) => v && setView(v)}
            className="border border-border rounded-lg bg-card">
            <ToggleGroupItem value="table" aria-label="Tabla" data-testid="view-table-btn">
              <HugeiconsIcon icon={Menu01Icon} size={16} />
            </ToggleGroupItem>
            <ToggleGroupItem value="cards" aria-label="Tarjetas" data-testid="view-cards-btn">
              <HugeiconsIcon icon={GridViewIcon} size={16} />
            </ToggleGroupItem>
          </ToggleGroup>
          <ToggleGroup
            type="single"
            value={density}
            onValueChange={(v) => v && setDensity(v)}
            className="border border-border rounded-lg bg-card">
            <ToggleGroupItem value="comfortable" aria-label="Cómoda" className="text-xs px-2.5">
              Cómoda
            </ToggleGroupItem>
            <ToggleGroupItem value="compact" aria-label="Compacta" className="text-xs px-2.5">
              Compacta
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        <div className="flex flex-wrap gap-2">
          {filterDefs.map((f) => (
            <Select key={f.key} value={filters[f.key]} onValueChange={(v) => setFilters((p) => ({ ...p, [f.key]: v }))}>
              <SelectTrigger className="w-auto min-w-36 h-8 text-xs bg-card" data-testid={`filter-${f.key}`}>
                <SelectValue placeholder={f.label} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{f.label}: todos</SelectItem>
                {f.options.map((o) => (
                  <SelectItem key={o} value={o}>
                    {o}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}
        </div>
      </div>

      {/* Aviso de filtro activo al llegar desde una tarjeta del Panel. */}
      {(() => {
        const active = [];
        if (filters.stage !== ALL) active.push(`Etapa: ${filters.stage.split(',').join(' o ')}`);
        if (filters.department !== ALL) active.push(`Departamento: ${filters.department}`);
        if (filters.risk !== ALL) active.push(`Riesgo: ${filters.risk}`);
        if (filters.provider !== ALL) active.push(`Proveedor: ${filters.provider}`);
        if (evaluacionFilter === 'abierta') active.push('Evaluación: abierta (pendiente o en curso)');
        if (active.length === 0) return null;
        return (
          <div className="flex flex-wrap items-center gap-2 mb-4 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2">
            <span className="text-xs text-muted-foreground">Filtrando por:</span>
            {active.map((a) => (
              <span key={a} className="text-xs font-medium text-primary bg-primary/10 rounded-full px-2 py-0.5">
                {a}
              </span>
            ))}
            {rows && (
              <span className="text-xs text-muted-foreground ml-auto">
                {rows.length} {rows.length === 1 ? 'resultado' : 'resultados'}
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => {
                setFilters({ department: ALL, stage: ALL, risk: ALL, provider: ALL, data_classification: ALL });
                navigate('/registro', { replace: true });
              }}>
              Limpiar
            </Button>
          </div>
        );
      })()}

      {!rows ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Database01Icon}
          title="Sin resultados"
          subtitle="Ajuste los filtros o registre una nueva iniciativa."
        />
      ) : view === 'table' ? (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <Table
            data-testid="registry-table"
            className={density === 'compact' ? '[&_td]:py-2 [&_th]:py-2' : '[&_td]:py-3.5 [&_th]:py-3'}>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead
                  className="text-xs uppercase tracking-wide text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors"
                  onClick={() => {
                    setSort((prev) => (prev === 'name' ? '-name' : 'name'));
                    setPage(1);
                  }}>
                  <span className="inline-flex items-center gap-1">
                    Iniciativa
                    {(sort === 'name' || sort === '-name') && (
                      <HugeiconsIcon
                        icon={sort.startsWith('-') ? ArrowDown01Icon : ArrowUp01Icon}
                        size={12}
                        className="text-primary"
                      />
                    )}
                  </span>
                </TableHead>
                <TableHead
                  className="text-xs uppercase tracking-wide text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors"
                  onClick={() => {
                    setSort((prev) => (prev === 'department' ? '-department' : 'department'));
                    setPage(1);
                  }}>
                  <span className="inline-flex items-center gap-1">
                    Departamento
                    {(sort === 'department' || sort === '-department') && (
                      <HugeiconsIcon
                        icon={sort.startsWith('-') ? ArrowDown01Icon : ArrowUp01Icon}
                        size={12}
                        className="text-primary"
                      />
                    )}
                  </span>
                </TableHead>
                <TableHead className="text-xs uppercase tracking-wide text-muted-foreground">Estado</TableHead>
                <TableHead
                  className="text-xs uppercase tracking-wide text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors"
                  onClick={() => {
                    setSort((prev) => (prev === '-next_review_date' ? 'next_review_date' : '-next_review_date'));
                    setPage(1);
                  }}>
                  <span className="inline-flex items-center gap-1">
                    Fechas
                    {(sort === '-next_review_date' || sort === 'next_review_date') && (
                      <HugeiconsIcon
                        icon={sort.startsWith('-') ? ArrowDown01Icon : ArrowUp01Icon}
                        size={12}
                        className="text-primary"
                      />
                    )}
                  </span>
                </TableHead>
                <TableHead className="text-xs uppercase tracking-wide text-muted-foreground text-right">
                  Acciones
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((r) => (
                <TableRow
                  key={r.id}
                  className="cursor-pointer group"
                  onClick={() => navigate(`/iniciativas/${r.id}`)}
                  data-testid={`registry-row-${r.id}`}>
                  <TableCell className="max-w-sm">
                    <p className="font-medium text-sm leading-tight">{r.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {r.code} · {r.business_owner}
                      {r.provider && ` · ${r.provider}`}
                      {r.model && ` (${r.model})`}
                      {r.from_shadow_ai && ' · Shadow AI'}
                    </p>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5 text-sm">
                      <HugeiconsIcon
                        icon={(DEPT_ICONS[r.department] || { icon: Settings02Icon }).icon}
                        size={14}
                        className={(DEPT_ICONS[r.department] || { color: 'text-muted-foreground' }).color}
                        aria-hidden="true"
                      />
                      {r.department}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1 text-xs">
                      <span className="font-semibold text-foreground/70">Etapa</span>
                      <StageBadge stage={r.stage} />
                      <span className="font-semibold text-foreground/70">Riesgo</span>
                      <RiskBadge level={r.residual_risk} />
                      <span className="font-semibold text-foreground/70">Comité</span>
                      <StatusBadge status={r.committee_status} />
                      <span className="font-semibold text-foreground/70">Prioridad</span>
                      <PortfolioPriorityBadge value={r.priority_class} />
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1 text-xs/5">
                      <span className="font-semibold text-foreground/70">Creación</span>
                      <span className="text-muted-foreground">{fmtDate(r.created_at)}</span>
                      <span className="font-semibold text-foreground/70">Conversión</span>
                      <span className="text-muted-foreground">{fmtDate(r.created_at)}</span>
                      <span className="font-semibold text-foreground/70">Revisión</span>
                      <span className="text-muted-foreground">{fmtDate(r.next_review_date)}</span>
                      <span className="font-semibold text-foreground/70">Compromiso</span>
                      <span className="text-muted-foreground">{fmtDate(r.next_milestone_date)}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex items-center rounded-lg border border-border divide-x divide-border bg-background">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2.5 text-xs font-medium rounded-none rounded-l-lg hover:bg-muted focus-visible:ring-0"
                        onClick={() => {
                          if (r.stage === 'Pausada') executeAction(r.id, 'resume');
                          else if (r.stage !== 'Archivada') executeAction(r.id, 'pause');
                        }}
                        disabled={r.stage === 'Archivada'}>
                        <HugeiconsIcon
                          icon={r.stage === 'Pausada' ? PlayIcon : PauseIcon}
                          size={14}
                          className="mr-1.5"
                          aria-hidden="true"
                        />
                        {r.stage === 'Pausada' ? 'Retomar' : 'Pausar'}
                      </Button>
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 rounded-none rounded-r-lg hover:bg-muted focus-visible:ring-0"
                            aria-label="Más acciones">
                            <HugeiconsIcon icon={MoreVerticalIcon} size={14} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" sideOffset={4} className="w-52">
                          {r.stage !== 'Pausada' && r.stage !== 'Archivada' && (
                            <DropdownMenuItem onClick={() => executeAction(r.id, 'pause')}>
                              <HugeiconsIcon icon={PauseIcon} size={14} className="text-warning" />
                              Pausar
                            </DropdownMenuItem>
                          )}
                          {r.stage === 'Pausada' && (
                            <DropdownMenuItem onClick={() => executeAction(r.id, 'resume')}>
                              <HugeiconsIcon icon={PlayIcon} size={14} className="text-success" />
                              Retomar
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => executeAction(r.id, 'return_to_demand')}>
                            <HugeiconsIcon icon={InboxIcon} size={14} className="text-info" />
                            Devolver a bandeja
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => executeAction(r.id, 'archive')}>
                            <HugeiconsIcon icon={Archive02Icon} size={14} className="text-muted-foreground" />
                            Archivar
                          </DropdownMenuItem>
                          {can.delete && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setDeleteTarget(r)}
                                className="text-destructive focus:text-destructive">
                                <HugeiconsIcon icon={Delete02Icon} size={14} />
                                Eliminar
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {/* Pagination */}
          {rows.length > PAGE_SIZE && (
            <div className="flex items-center justify-between border-t border-border px-4 py-3">
              <p className="text-xs text-muted-foreground">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, rows.length)} de {rows.length}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}>
                  Anterior
                </Button>
                {Array.from({ length: Math.ceil(rows.length / PAGE_SIZE) }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === Math.ceil(rows.length / PAGE_SIZE) || Math.abs(p - page) <= 1)
                  .map((p, idx, arr) => (
                    <span key={p}>
                      {idx > 0 && arr[idx - 1] !== p - 1 && (
                        <span className="text-xs text-muted-foreground px-1">…</span>
                      )}
                      <Button
                        variant={page === p ? 'default' : 'outline'}
                        size="sm"
                        className="h-7 w-7 px-0 text-xs"
                        onClick={() => setPage(p)}>
                        {p}
                      </Button>
                    </span>
                  ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  disabled={page >= Math.ceil(rows.length / PAGE_SIZE)}
                  onClick={() => setPage((p) => p + 1)}>
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="registry-cards">
          {rows.map((r) => (
            <Card key={r.id} className="shadow-card border-border hover:shadow-float transition-shadow flex flex-col">
              <CardContent className="p-5 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">
                      {r.code} · {r.asset_type}
                    </p>
                    <p className="font-display font-semibold mt-0.5 leading-snug">{r.name}</p>
                  </div>
                  <HugeiconsIcon icon={TestTube01Icon} size={16} className="text-muted-foreground shrink-0 mt-1" />
                </div>
                <p className="text-xs text-muted-foreground mt-2 line-clamp-2 leading-relaxed">{r.description}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-4">
                  <StageBadge stage={r.stage} />
                  <RiskBadge level={r.residual_risk} />
                  <PortfolioPriorityBadge value={r.priority_class} />
                </div>
                <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                  <span className="truncate">
                    {r.department} · {r.business_owner}
                  </span>
                </div>
                <Button asChild variant="outline" size="sm" className="w-full mt-auto border-border">
                  <Link to={`/iniciativas/${r.id}`}>Abrir ficha</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {rows && <p className="text-xs text-muted-foreground mt-4">{rows.length} iniciativa(s) en el portafolio</p>}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar iniciativa</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de eliminar <span className="font-medium text-foreground">{deleteTarget?.name}</span>? La
              iniciativa desaparecerá de todos los listados. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteInitiative(deleteTarget?.id)}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
